import { useCallback, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { SimpleMenu } from '@/components/MenuGrid';
import { useLocale } from '@/context/LocaleContext';
import { useItemTooltipHover } from '@/hooks/useItemTooltipHover';
import { useDropLogs } from '@/hooks/useDropLogs';
import type { AuthUser, PlayerData } from '@/vite-env.d';
import {
  DropItemChatModal,
  DropLogChatButton,
  type DropLogChatItem,
} from '@/pages/logsystem/DropItemChatModal';
import {
  filterDropLogs,
  getDropLogPageCount,
  getWeaponOptionsForRace,
  paginateDropLogs,
  type DropLogFilterState,
  type DropLogRaceFilter,
  type DropLogTimeFilter,
} from '@/lib/dropLogFilters';
import { formatDropLogDate, formatDropLogTimeAgo } from '@/lib/dropLogFormat';
import { characterAvatarUrl, resolveMediaUrl } from '@/lib/assetUrl';
import '@/styles/ranking-layout.css';
import '@/styles/drop-log-layout.css';
import { DropLogPagination } from './DropLogPagination';

export function DropLogPage() {
  const { t, locale } = useLocale();
  const { user } = useOutletContext<{ user: AuthUser; player: PlayerData | null }>();
  const { data: drops, isLoading, isError } = useDropLogs();
  const [chatItem, setChatItem] = useState<DropLogChatItem | null>(null);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [draftRace, setDraftRace] = useState<DropLogRaceFilter>('');
  const [draftType, setDraftType] = useState('');
  const [draftTime, setDraftTime] = useState<DropLogTimeFilter>('all');
  const [applied, setApplied] = useState<Omit<DropLogFilterState, 'search'>>({
    race: '',
    typePattern: '',
    time: 'all',
  });

  const filters: DropLogFilterState = useMemo(
    () => ({
      search,
      race: applied.race,
      typePattern: applied.typePattern,
      time: applied.time,
    }),
    [search, applied],
  );

  const filtered = useMemo(() => filterDropLogs(drops ?? [], filters), [drops, filters]);
  const pages = getDropLogPageCount(filtered.length);
  const safePage = Math.min(page, pages);
  const visibleRows = paginateDropLogs(filtered, safePage);

  const tooltipKey = `${safePage}-${filtered.length}-${visibleRows.map((r) => r.id64).join(',')}`;
  useItemTooltipHover(!isLoading && !isError && visibleRows.length > 0, tooltipKey);

  const weaponOptions = getWeaponOptionsForRace(draftRace);

  const handleRaceChange = useCallback((race: DropLogRaceFilter) => {
    setDraftRace(race);
    setDraftType('');
  }, []);

  const handleApply = useCallback(() => {
    setApplied({
      race: draftRace,
      typePattern: draftType,
      time: draftTime,
    });
    setPage(1);
  }, [draftRace, draftType, draftTime]);

  const handleReset = useCallback(() => {
    setSearch('');
    setDraftRace('');
    setDraftType('');
    setDraftTime('all');
    setApplied({ race: '', typePattern: '', time: 'all' });
    setPage(1);
  }, []);

  return (
    <div className="container-ingame fade-in">
      <div className="calendar-container ranking-page drop-log-page">
        <SimpleMenu title={t('drop_log')} />

        <div className="calendar-info-container">
          <div className="ranking-filters-container">
            <div className="ranking-filters">
              <div className="ranking-filter-group">
                <input
                  type="text"
                  id="customSearchBox"
                  aria-label={t('log_search_placeholder')}
                  className="ranking-filter-input"
                  placeholder={t('log_search_placeholder')}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>

              <div className="ranking-filter-group">
                <select
                  id="filter-race"
                  aria-label={t('log_filter_race')}
                  className="ranking-filter-select browser-default"
                  value={draftRace}
                  onChange={(e) => handleRaceChange(e.target.value as DropLogRaceFilter)}
                >
                  <option value="">{t('log_filter_race')}</option>
                  <option value="chinese">{t('log_filter_race_chinese')}</option>
                  <option value="euro">{t('log_filter_race_euro')}</option>
                </select>
              </div>

              <div className="ranking-filter-group">
                <select
                  id="filter-type"
                  aria-label={t('log_filter_type')}
                  className="ranking-filter-select browser-default"
                  value={draftType}
                  disabled={!draftRace}
                  onChange={(e) => setDraftType(e.target.value)}
                >
                  <option value="">{t('log_filter_type')}</option>
                  {weaponOptions.map((weapon) => (
                    <option key={weapon.pattern} value={weapon.pattern}>
                      {weapon.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ranking-filter-group">
                <select
                  id="filter-time"
                  aria-label={t('log_filter_time_all')}
                  className="ranking-filter-select browser-default"
                  value={draftTime}
                  onChange={(e) => setDraftTime(e.target.value as DropLogTimeFilter)}
                >
                  <option value="all">{t('log_filter_time_all')}</option>
                  <option value="today">{t('log_filter_time_today')}</option>
                  <option value="week">{t('log_filter_time_week')}</option>
                  <option value="month">{t('log_filter_time_month')}</option>
                </select>
              </div>

              <button
                type="button"
                id="apply-filters-btn"
                className="ranking-filter-btn ranking-filter-btn-apply ranking-filter-btn-label"
                onClick={handleApply}
              >
                {t('log_filter_apply')}
              </button>

              <button
                type="button"
                id="reset-filters-btn"
                className="ranking-filter-btn ranking-filter-btn-reset ranking-filter-btn-label"
                onClick={handleReset}
              >
                {t('log_filter_reset')}
              </button>
            </div>
          </div>

          {isLoading ? (
            <div style={{ textAlign: 'center', color: '#a97c50', fontSize: '1.2rem', padding: '40px' }}>
              {t('loading_data')}
            </div>
          ) : isError ? (
            <div style={{ textAlign: 'center', color: '#a97c50', fontSize: '1.2rem', padding: '40px' }}>
              {t('log_load_error')}
            </div>
          ) : (
            <>
              <div className="ranking-table-section">
                <div className="log-table-wrapper ranking-table-wrapper">
                  <table className="drop-table">
                    <colgroup>
                      <col className="drop-col-icon" />
                      <col className="drop-col-name" />
                      <col className="drop-col-char" />
                      <col className="drop-col-monster" />
                      <col className="drop-col-map" />
                      <col className="drop-col-date" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="drop-item-cell" colSpan={2}>
                          {t('log_table_item')}
                        </th>
                        <th className="drop-character-cell">{t('log_table_character')}</th>
                        <th className="drop-col-monster-header">{t('log_table_monster')}</th>
                        <th className="drop-map-cell">{t('log_table_map')}</th>
                        <th className="drop-date-col">{t('log_table_date')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.length > 0 ? (
                        visibleRows.map((row) => {
                        const ago = formatDropLogTimeAgo(row.dropDate, t);
                        return (
                          <tr
                            key={`${row.id64}-${row.dropDate}`}
                            data-dropdate={row.dropDate}
                            data-codename={row.codeName128}
                          >
                            <td className="drop-item-cell">
                              <div
                                className="drop-item-icon-wrapper item-with-tooltip"
                                data-id64={String(row.id64)}
                              >
                                <img
                                  src={resolveMediaUrl(`/${row.itemIcon}`)}
                                  alt=""
                                  className="drop-item-icon"
                                  loading="lazy"
                                />
                                {row.isLocked ? <div className="drop-item-lock-icon" /> : null}
                                {row.isRare ? <div className="drop-item-plus" /> : null}
                              </div>
                            </td>
                            <td className="drop-itemname-cell" title={row.itemName}>{row.itemName}</td>
                            <td className="drop-character-cell">
                              <div className="drop-character-row">
                                <span className="drop-character-avatar">
                                <img
                                  src={characterAvatarUrl(row.refObjId)}
                                  alt=""
                                  className="drop-char-icon"
                                  loading="lazy"
                                />
                                  {row.charId > 0 && row.charId !== user.charId ? (
                                    <DropLogChatButton
                                      title={t('log_chat_button_title')}
                                      onClick={() =>
                                        setChatItem({
                                          id64: row.id64,
                                          charId: row.charId,
                                          charName16: row.charName16,
                                          itemName: row.itemName,
                                          itemIcon: row.itemIcon,
                                          refObjId: row.refObjId,
                                        })
                                      }
                                    />
                                  ) : null}
                                </span>
                                <span className="drop-char-name" title={row.charName16}>{row.charName16}</span>
                              </div>
                            </td>
                            <td className="drop-monster-cell">
                              {row.monsterName ?? '—'}
                            </td>
                            <td className="drop-map-cell" title={row.map}>{row.map}</td>
                            <td className="drop-date-cell">
                              <span className="drop-date-text">
                                {formatDropLogDate(row.dropDate, locale)}
                              </span>
                              {ago ? (
                                <>
                                  <span className="drop-date-sep"> | </span>
                                  <span className="drop-date-ago">{ago}</span>
                                </>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })
                      ) : (
                        <tr>
                          <td colSpan={6} style={{ textAlign: 'center', color: '#a97c50', padding: '24px' }}>
                            {t('log_no_results')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <div className="ranking-table-footer">
                    <DropLogPagination
                      page={safePage}
                      pages={pages}
                      onPageChange={(next) => setPage(next)}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <DropItemChatModal
        item={chatItem}
        currentCharId={user.charId}
        onClose={() => setChatItem(null)}
      />
    </div>
  );
}

// Keep default export path stable for router
export default DropLogPage;
